import CourseServiceApi from '@/api/courseServiceApi';
import ElementServiceApi from '@/api/elementServiceApi';
import ModuleServiceApi from '@/api/moduleServiceApi';
import { trainingsMode } from '@/shared/enums';

const state = {
  courses: {
    items: [],
    count: 0,
    loading: false,
    page: 1,
    search: '',
    pagesLength: 0,
    take: 8
  },
  activeMode: trainingsMode.DisplayCourses,
  activeCourseEditTree: {
    loading: false,
    activeElement: {
      type: null,
      element: null,
      path: null,
      definition: null
    },
    course: null,
    courseInsertPayload: null, // for handling remove action after add
    hiddenElements: []
  }
};

const resetActiveCourseEditTree = () => {
  return {
    loading: false,
    activeElement: {
      type: null,
      element: null,
      path: null,
      definition: null
    },
    course: null,
    courseInsertPayload: null, // for handling remove action after add
    hiddenElements: []
  };
};

const getters = {
  breadcrumbCourse: state => state.activeCourseEditTree.course && state.activeCourseEditTree.course.name
};

const actions = {
  async getAllCourses({ commit, rootState, state }, payload) {
    commit('SAVE_COURSES_LOADING', { loading: true });

    try {
      payload = payload || { page: state.courses.page, search: state.courses.search };

      let skip = (payload.page - 1) * state.courses.take;
      let { data } = await new CourseServiceApi(rootState.account.token).getCourses(
        payload.search,
        skip,
        state.courses.take
      );

      if (data.result.items.length === 0 && data.result.totalCount > 0) {
        payload.page--;
        skip = (payload.page - 1) * state.courses.take;
        ({ data } = await new CourseServiceApi(rootState.account.token).getCourses(
          payload.search,
          skip,
          state.courses.take
        ));
      }

      commit('SAVE_COURSES', {
        courses: data.result.items,
        page: payload.page,
        pagesLength: Math.ceil(data.result.totalCount / state.courses.take),
        totalCount: data.result.totalCount,
        search: payload.search
      });
    } catch (e) {
      // TODO: handle
      console.error(e);
    } finally {
      commit('SAVE_COURSES_LOADING', { loading: false });
    }
  },
  switchActiveMode({ commit }, payload) {
    commit('CHANGE_ACTIVE_STATE', { mode: payload.mode });
  },
  async requestModuleEdit({ commit, rootState }, payload) {
    commit('CREATE_EDIT_TREE_LOADING', { loading: true });

    try {
      const subElementsResponse = await new CourseServiceApi(rootState.account.token).getWithSubmodules(payload.id);
      commit('CREATE_EDIT_TREE', {
        course: subElementsResponse.data.result,
        hiddenElements: state.activeCourseEditTree.hiddenElements
      });
      return true;
    } catch (e) {
      // TODO: handle
      console.error(e);
      return false;
    } finally {
      commit('CREATE_EDIT_TREE_LOADING', { loading: false });
    }
  },
  requestModuleEditClear({ commit }) {
    commit('CREATE_EDIT_TREE', { course: null });
  },
  async updateTree({ commit, rootState, dispatch, state }, payload) {
    try {
      if (payload.action.moved) {
        await new ElementServiceApi(rootState.account.token).reorder({
          id: payload.action.moved.element.id,
          parentId: payload.action.moved.element.parentId,
          order: payload.action.moved.newIndex
        });
        await dispatch('requestModuleEdit', { id: payload.courseId });
      } else if (payload.action.added) {
        if (payload.action.added.element.newElementType) {
          switch (payload.action.added.element.newElementType) {
            case 'module':
              await new ModuleServiceApi(rootState.account.token).create({
                name: 'Moduł',
                parentId: payload.parentId,
                order: payload.action.added.newIndex,
                courseId: payload.courseId
              });
              break;
            case 'lesson':
              await new ElementServiceApi(rootState.account.token).createLesson({
                name: 'Lekcja',
                parentId: payload.parentId,
                order: payload.action.added.newIndex,
                courseId: payload.courseId
              });
              break;
            case 'meeting':
              await new ElementServiceApi(rootState.account.token).createMeeting({
                name: 'Spotkanie',
                parentId: payload.parentId,
                order: payload.action.added.newIndex,
                courseId: payload.courseId
              });
              break;
            case 'file':
              await new ElementServiceApi(rootState.account.token).createFile({
                name: 'Plik',
                parentId: payload.parentId,
                order: payload.action.added.newIndex,
                courseId: payload.courseId
              });
              break;
            case 'video':
              await new ElementServiceApi(rootState.account.token).createVideo({
                name: 'Video',
                parentId: payload.parentId,
                order: payload.action.added.newIndex,
                courseId: payload.courseId
              });
              break;
            default:
              throw 'Unknown new item in tree';
          }
          await dispatch('requestModuleEdit', { id: payload.courseId });
        } else {
          commit('SAVE_STACKED_ADD', payload);
        }
      } else if (payload.action.removed) {
        await new ElementServiceApi(rootState.account.token).reorder({
          id: state.activeCourseEditTree.courseInsertPayload.action.added.element.id,
          parentId: state.activeCourseEditTree.courseInsertPayload.parentId,
          order: state.activeCourseEditTree.courseInsertPayload.action.added.newIndex
        });
        await dispatch('requestModuleEdit', { id: payload.courseId });
      } else {
        // noinspection ExceptionCaughtLocallyJS
        throw 'Unhandled execution path';
      }
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
  },
  async removeCourseElement({ rootState, dispatch }, payload) {
    try {
      if (payload.element.isModule) {
        await new ModuleServiceApi(rootState.account.token).delete(payload.element.id);
      } else {
        await new ElementServiceApi(rootState.account.token).delete(payload.element.id);
      }
    } catch (e) {
      // TODO: handle
      console.error(e);
    }

    await dispatch('requestModuleEdit', { id: payload.courseId });
  },
  toggleShowElements({ commit }, payload) {
    commit('TOGGLE_VISIBLE_ELEMENTS', { path: payload.path });
  },
  requestElementEdit({ commit }, payload) {
    commit('SET_ACTIVE_EDIT_ELEMENT', payload);
  },
  removeElement({ commit }, payload) {
    commit('REMOVE_ELEMENT', payload);
  },
  async requestElementDefinition({ commit, rootState }, payload) {
    try {
      const elementResponse = await new ElementServiceApi(rootState.account.token).get(payload.id);

      commit('SET_ACTIVE_EDIT_ELEMENT_DEFINITION', {
        definition: elementResponse.data.result.definition
      });
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
  },
  resetActiveElement({ commit }) {
    commit('RESET_ACTIVE_ELEMENT');
  },
  async saveModule({ commit, rootState, dispatch }, payload) {
    try {
      await new ModuleServiceApi(rootState.account.token).update({
        id: payload.instance.id,
        order: payload.instance.order,
        parentId: payload.instance.parentId,
        name: payload.name
      });
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
    commit('RESET_ACTIVE_ELEMENT');
    await dispatch('requestModuleEdit', { id: payload.courseId });
  },
  async saveMeeting({ commit, rootState, dispatch }, payload) {
    try {
      await new ElementServiceApi(rootState.account.token).updateMeeting({
        id: payload.instance.id,
        order: payload.instance.order,
        parentId: payload.instance.parentId,
        name: payload.name,
        isRequired: payload.isRequired,
        definition: { ...payload.definition, ...payload.newDefinition }
      });
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
    commit('RESET_ACTIVE_ELEMENT');
    await dispatch('requestModuleEdit', { id: payload.courseId });
  },
  async saveFile({ commit, rootState, dispatch }, payload) {
    try {
      await new ElementServiceApi(rootState.account.token).updateFile({
        id: payload.instance.id,
        order: payload.instance.order,
        parentId: payload.instance.parentId,
        name: payload.name,
        isRequired: payload.isRequired,
        definition: { ...payload.definition, ...payload.newDefinition }
      });
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
    commit('RESET_ACTIVE_ELEMENT');
    await dispatch('requestModuleEdit', { id: payload.courseId });
  },
  async saveVideo({ commit, rootState, dispatch }, payload) {
    try {
      await new ElementServiceApi(rootState.account.token).updateVideo({
        id: payload.instance.id,
        order: payload.instance.order,
        parentId: payload.instance.parentId,
        name: payload.name,
        isRequired: payload.isRequired,
        definition: { ...payload.definition, ...payload.newDefinition }
      });
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
    commit('RESET_ACTIVE_ELEMENT');
    await dispatch('requestModuleEdit', { id: payload.courseId });
  },
  async saveLesson({ commit, rootState, dispatch }, payload) {
    try {
      await new ElementServiceApi(rootState.account.token).updateLesson({
        id: payload.instance.id,
        order: payload.instance.order,
        parentId: payload.instance.parentId,
        name: payload.name,
        isRequired: payload.isRequired,
        definition: { ...payload.definition, ...payload.newDefinition }
      });
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
    commit('RESET_ACTIVE_ELEMENT');
    await dispatch('requestModuleEdit', { id: payload.courseId });
  },
  async createNewVersion({ rootState, dispatch }, payload) {
    try {
      await new CourseServiceApi(rootState.account.token).update({
        id: payload.id,
        name: payload.name
      });

      await dispatch('requestModuleEdit', { id: payload.id });
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
  },
  async updateCourse({ rootState, dispatch }, payload) {
    try {
      await new CourseServiceApi(rootState.account.token).simpleUpdate(payload.id, payload.name, payload.isActive);

      await dispatch('requestModuleEdit', { id: payload.id });
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
  },
  async cloneCourse({ rootState, dispatch }, payload) {
    try {
      await new CourseServiceApi(rootState.account.token).copy(payload.courseId, payload.name);

      await dispatch('getAllCourses');
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
  },
  async finishEditing({ rootState, dispatch }, payload) {
    try {
      await new CourseServiceApi(rootState.account.token).finishEditing(payload.id);

      await dispatch('requestModuleEdit', { id: payload.id });
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
  },
  async removeCourse({ rootState, dispatch }, payload) {
    try {
      await new CourseServiceApi(rootState.account.token).delete(payload.id);

      await dispatch('getAllCourses');
    } catch (e) {
      // TODO: handle
      console.error(e);
    }
  }
};

const mutations = {
  SAVE_COURSES(state, payload) {
    state.courses = {
      ...state.courses,
      items: payload.courses,
      page: payload.page,
      pagesLength: payload.pagesLength,
      count: payload.totalCount,
      search: payload.search
    };
  },
  SAVE_COURSES_LOADING(state, payload) {
    state.courses = {
      ...state.courses,
      loading: payload.loading
    };
  },
  CHANGE_ACTIVE_STATE(state, payload) {
    state.activeMode = payload.mode;
  },
  CREATE_EDIT_TREE(state, payload) {
    if (payload.course && payload.course.children) {
      const mapChilds = (tree, path) => {
        const { children, elements, ...props } = tree;
        let newChildren = [];
        if (children && children.length) {
          newChildren = newChildren.concat(children.map((child, index) => mapChilds(child, [...path, index])));
        }
        if (elements && elements.length) {
          newChildren = newChildren.concat(
            elements.map(element => {
              element.isModule = false;
              element.newDefinition = null;
              switch (element.type) {
                case 1: // lesson
                  element.icon = 'mdi-school';
                  break;
                case 2: // video
                  element.icon = 'mdi-video';
                  break;
                case 3: // file
                  element.icon = 'mdi-file-multiple';
                  break;
                case 4: // meeting
                  element.icon = 'mdi-account-multiple';
                  break;
              }
              return element;
            })
          );
        }

        const result = { children: newChildren, ...props };
        result.isModule = true;
        result.children = result.children.sort((a, b) => a.order - b.order);
        result.icon = 'mdi-folder';
        result.showSubElements = !state.activeCourseEditTree.hiddenElements.includes(result.id);
        return result;
      };

      const course = mapChilds(payload.course, []);
      state.activeCourseEditTree = {
        ...state.activeCourseEditTree,
        course: course,
        activeElement: {
          ...state.activeCourseEditTree.activeElement,
          type: 'course',
          element: course,
          path: [0]
        }
      };
    } else {
      state.activeCourseEditTree = resetActiveCourseEditTree();
    }
  },
  CREATE_EDIT_TREE_LOADING(state, payload) {
    state.activeCourseEditTree = {
      ...state.activeCourseEditTree,
      loading: payload.loading
    };
  },
  SAVE_STACKED_ADD(state, payload) {
    state.activeCourseEditTree = {
      ...state.activeCourseEditTree,
      courseInsertPayload: payload
    }; // save for stack with removed event
  },
  TOGGLE_VISIBLE_ELEMENTS(state, payload) {
    const course = { ...state.activeCourseEditTree.course };
    const hiddenElements = [...state.activeCourseEditTree.hiddenElements];
    let element = course;

    for (let index of payload.path) {
      element = element.children;
      element = element[index];
    }

    element.showSubElements = !element.showSubElements;

    if (element.showSubElements) {
      hiddenElements.splice(hiddenElements.indexOf(element.id), 1);
    } else {
      hiddenElements.push(element.id);
    }

    state.activeCourseEditTree = {
      ...state.activeCourseEditTree,
      course: course,
      hiddenElements: hiddenElements
    };
  },
  SET_ACTIVE_EDIT_ELEMENT(state, payload) {
    state.activeCourseEditTree = {
      ...state.activeCourseEditTree,
      activeElement: {
        ...state.activeCourseEditTree.activeElement,
        type: payload.type,
        element: payload.element,
        path: payload.path
      }
    };
  },
  REMOVE_ELEMENT(state, payload) {
    const course = state.activeCourseEditTree.course;
    let section = course.children;
    for (let i = 0; i < payload.path.length - 1; i++) {
      section = section[payload.path[i]].children;
    }

    section.splice(payload.path[payload.path.length - 1], 1);

    state.activeCourseEditTree = {
      ...state.activeCourseEditTree,
      course: course
    };
  },
  SET_ACTIVE_EDIT_ELEMENT_DEFINITION(state, payload) {
    state.activeCourseEditTree = {
      ...state.activeCourseEditTree,
      activeElement: {
        ...state.activeCourseEditTree.activeElement,
        definition: payload.definition
      }
    };
  },
  RESET_ACTIVE_ELEMENT(state) {
    state.activeCourseEditTree = {
      ...state.activeCourseEditTree,
      activeElement: {
        ...state.activeCourseEditTree.activeElement,
        type: 'course',
        element: state.activeCourseEditTree.course,
        path: [0]
      }
    };
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};

// const moveElement = (array, oldIndex, newIndex) => {
//   if (newIndex >= array.length) {
//     var k = newIndex - array.length + 1;
//     while (k--) {
//       array.push(undefined);
//     }
//   }
//   array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
//   return array;
// };
//
// const lowerPath = (path1, path2) => {
//   if (path1.length !== path2.length) {
//     return path1.length > path2.length ? -1 : 1;
//   }
//
//   for (let i = 0; i < path1.length; i++) {
//     if (path1[i] > path2[i]) {
//       return -1;
//     } else if (path1[i] < path2[i]) {
//       return 1;
//     }
//   }
//
//   return 0;
// };
//
// const invokeInOrder = (fun1, fun2, order) => {
//   if (order === 'asc') {
//     fun1();
//     fun2();
//   } else {
//     fun2();
//     fun1();
//   }
// };
